import { ResourceType, ResourceTypePayload } from '@cbgms/api/modules/vehicles'

/**
 * Determines the resource type based on the current navigation URL
 * @param pathname - The current pathname from browser location
 * @param id - The resource ID (UUID)
 * @returns An object with type and id, or null if no match
 */

export const getResourceType = (pathname: string, id: string): ResourceTypePayload | undefined => {
  if (!pathname || !id) {
    return undefined
  }

  // Remove hash fragments for cleaner matching
  const pathWithoutHash = pathname.split('#')[0]
  const hashFragment = pathname.includes('#') ? pathname.split('#')[1] : ''

  // view_combined_invoices -> { "Type": "VCI", "Id": "<id or uuid>" }
  // Patterns: /history/invoices, #invoice_history, #order-history, #open-invoices, /workorders-invoices
  if (pathWithoutHash.includes('/history/invoices') || hashFragment === 'invoice_history' || hashFragment === 'order-history') {
    return { Type: ResourceType.ViewCombinedInvoices, Id: id }
  }

  // gms_vehicle_quotation -> { "Type": "GVQ", "Id": "<id or uuid>" }
  // Patterns: /vehicle-sales/quotations, #order-quotations, /workorders-quotations
  if (
    pathWithoutHash.includes('/vehicle-sales/quotations') ||
    pathWithoutHash.includes('/workorders-quotations') ||
    hashFragment === 'order-quotations'
  ) {
    return { Type: ResourceType.GmsVehicleQuotation, Id: id }
  }

  // gms_vehicle_invoice -> { "Type": "GVI", "Id": "<id or uuid>" }
  // Patterns: /vehicle-sales/invoices, #open-invoices, /workorders-invoices, /rental-vehicles/invoices
  if (
    hashFragment === 'open-invoices' ||
    pathWithoutHash.includes('/workorders-invoices') ||
    pathWithoutHash.includes('/rental-vehicles/invoices') ||
    pathWithoutHash.includes('/vehicle-sales/invoices')
  ) {
    return { Type: ResourceType.GmsVehicleInvoice, Id: id }
  }

  // gms_vehicle (default/existing) -> { "Type": "GMV", "Id": "<id or uuid>" }
  // Patterns: /vehicles, /courtesy-vehicles/vehicles, /rental-vehicles/vehicles
  if (
    pathWithoutHash.includes('/vehicles') ||
    pathWithoutHash.includes('/courtesy-vehicles/vehicles') ||
    pathWithoutHash.includes('/rental-vehicles/vehicles')
  ) {
    return { Type: ResourceType.GmsVehicle, Id: id }
  }

  // accounts patterns - check for specific hash fragments
  if (pathWithoutHash.includes('/accounts/')) {
    if (hashFragment === 'order-history' || hashFragment === 'open-invoices') {
      return { Type: ResourceType.ViewCombinedInvoices, Id: id }
    }
    if (hashFragment === 'order-quotations') {
      return { Type: ResourceType.GmsVehicleQuotation, Id: id }
    }
  }

  return undefined
}
